import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toDataURL as qrcodeToDataURL } from 'qrcode';
import { useAuthStore } from '../stores/authStore';

interface MfaEnrollScreenProps {
  /** Where to navigate after a successful enrollment. */
  redirectTo: string;
}

/**
 * First-login MFA enrollment. Fetches the otpauth URI + recovery codes on
 * mount, renders a QR code + the base32 secret + the one-shot recovery
 * codes, then prompts for a TOTP code to confirm.
 *
 * Security: `enrollment` (secret + recovery codes) and `mfaChallengeToken`
 * are in-memory only in the store — a tab reload abandons the flow by
 * design, matching the server's short-lived challenge token.
 */
export default function MfaEnrollScreen({ redirectTo }: MfaEnrollScreenProps) {
  const navigate = useNavigate();

  const enrollment = useAuthStore((s) => s.enrollment);
  const error = useAuthStore((s) => s.error);
  const adminUser = useAuthStore((s) => s.adminUser);
  const token = useAuthStore((s) => s.token);
  const beginEnrollment = useAuthStore((s) => s.beginEnrollment);
  const confirmEnrollment = useAuthStore((s) => s.confirmEnrollment);
  const logout = useAuthStore((s) => s.logout);

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [qrDataUri, setQrDataUri] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Trigger the enrollment fetch exactly once on mount. React StrictMode
  // double-invokes effects in dev, so we gate with a ref to avoid firing
  // the POST twice (each fetch invalidates the last `secret`).
  const beganRef = useRef(false);
  useEffect(() => {
    if (beganRef.current) return;
    if (enrollment) return; // already loaded
    beganRef.current = true;
    void beginEnrollment().catch(() => {
      /* error surfaced via store.error */
    });
  }, [beginEnrollment, enrollment]);

  // Render the otpauth URI to a data-uri PNG for <img>.
  useEffect(() => {
    if (!enrollment?.otpauthUri) {
      setQrDataUri(null);
      return;
    }
    let cancelled = false;
    qrcodeToDataURL(enrollment.otpauthUri, { margin: 1, width: 224 })
      .then((dataUri) => {
        if (!cancelled) setQrDataUri(dataUri);
      })
      .catch(() => {
        if (!cancelled) setQrDataUri(null);
      });
    return () => {
      cancelled = true;
    };
  }, [enrollment?.otpauthUri]);

  // Fully authenticated — enrollment succeeded. Navigate out.
  useEffect(() => {
    if (token && adminUser) {
      navigate(redirectTo, { replace: true });
    }
  }, [token, adminUser, navigate, redirectTo]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    try {
      await confirmEnrollment(code.trim());
    } catch {
      // Error already recorded in store.
    } finally {
      setSubmitting(false);
    }
  }

  async function handleStartOver() {
    await logout();
    navigate('/login', { replace: true });
  }

  async function handleCopySecret() {
    if (!enrollment?.secret) return;
    try {
      await navigator.clipboard.writeText(enrollment.secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard denied — ignore silently, the secret is visible.
    }
  }

  function handleDownloadRecoveryCodes() {
    if (!enrollment?.recoveryCodes?.length) return;
    const body = [
      'Word Clash Admin — MFA Recovery Codes',
      '-----------------------------------------',
      'Store these somewhere safe. Each code works ONCE.',
      '',
      ...enrollment.recoveryCodes,
      '',
    ].join('\n');
    const blob = new Blob([body], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'word-clash-admin-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-100 px-4 py-10"
      data-testid="mfa-enroll-screen"
    >
      <div className="w-full max-w-xl bg-white rounded-lg shadow p-6 space-y-6">
        <div className="text-center space-y-1">
          <h1 className="text-xl font-semibold text-slate-900">
            Set up two-factor authentication
          </h1>
          <p className="text-sm text-slate-500">
            Every admin account is protected by TOTP. This setup is shown
            once — save the recovery codes before you confirm.
          </p>
        </div>

        {!enrollment && !error && (
          <div
            className="text-sm text-slate-500 text-center"
            data-testid="mfa-enroll-loading"
          >
            Generating enrollment…
          </div>
        )}

        {enrollment && (
          <>
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-700">
                1. Scan the QR code
              </h2>
              <div
                className="flex justify-center"
                data-testid="mfa-enroll-qr"
              >
                {qrDataUri ? (
                  <img
                    src={qrDataUri}
                    alt="TOTP QR code"
                    width={224}
                    height={224}
                    className="rounded border border-slate-200"
                  />
                ) : (
                  <div className="w-56 h-56 bg-slate-100 rounded border border-slate-200 flex items-center justify-center text-xs text-slate-400">
                    Rendering…
                  </div>
                )}
              </div>
              <div className="text-xs text-slate-500 text-center">
                Can't scan? Enter this secret manually:
              </div>
              <div className="flex items-center gap-2">
                <code
                  className="flex-1 block text-center font-mono text-sm bg-slate-50 border border-slate-200 rounded px-3 py-2 break-all"
                  data-testid="mfa-enroll-secret"
                >
                  {enrollment.secret}
                </code>
                <button
                  type="button"
                  onClick={handleCopySecret}
                  data-testid="mfa-enroll-copy-secret"
                  className="text-xs rounded px-2.5 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-700">
                2. Save your recovery codes
              </h2>
              <p className="text-xs text-slate-500">
                Each code works <strong>once</strong>. They are shown{' '}
                <strong>only now</strong> — store them in a password manager
                or download them below.
              </p>
              <ul
                data-testid="mfa-enroll-recovery-codes"
                className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded p-3"
              >
                {enrollment.recoveryCodes.map((rc) => (
                  <li
                    key={rc}
                    className="font-mono text-sm text-slate-800 tracking-wider"
                    data-testid="mfa-enroll-recovery-code"
                  >
                    {rc}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={handleDownloadRecoveryCodes}
                data-testid="mfa-enroll-download"
                className="text-xs rounded px-2.5 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Download as .txt
              </button>
            </section>

            <form onSubmit={handleSubmit} className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-700">
                3. Confirm with a code from your authenticator
              </h2>
              <label className="block text-sm">
                <span className="sr-only">Authentication code</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={10}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  disabled={submitting}
                  placeholder="6-digit code"
                  data-testid="mfa-enroll-code"
                  className="block w-full rounded border border-slate-300 px-3 py-2 font-mono tracking-widest text-center text-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50"
                />
              </label>

              {error && (
                <div
                  role="alert"
                  data-testid="mfa-enroll-error"
                  className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
                >
                  {error}
                </div>
              )}

              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={submitting || !code.trim()}
                  data-testid="mfa-enroll-confirm"
                  className="flex-1 rounded bg-slate-800 text-white py-2 font-medium hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50"
                >
                  {submitting ? 'Confirming…' : 'Confirm & sign in'}
                </button>
                <button
                  type="button"
                  onClick={handleStartOver}
                  data-testid="mfa-enroll-start-over"
                  className="rounded border border-slate-300 text-slate-700 px-3 py-2 text-sm hover:bg-slate-50"
                >
                  Start over
                </button>
              </div>
            </form>
          </>
        )}

        {!enrollment && error && (
          <div className="space-y-3">
            <div
              role="alert"
              data-testid="mfa-enroll-error"
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
            >
              {error}
            </div>
            <button
              type="button"
              onClick={handleStartOver}
              data-testid="mfa-enroll-start-over"
              className="w-full rounded border border-slate-300 text-slate-700 py-2 text-sm hover:bg-slate-50"
            >
              Start over
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
